import React from 'react'
import { RichText } from '../../elements/RichText/component'
import { Text } from '../../elements/Text'
import { IProps } from './index'
import { Icon, renderIconSVG } from '../../utils'
import { Tooltip } from '../../elements/Tooltip'
import CustomerService from 'marketing-site/src/images/customer-service.svg'
import WhatsAppIcon from 'marketing-site/src/images/whatsapp-icon.svg'
import SwitchIcon from 'marketing-site/src/images/switch-icon.svg'
import { ADD_ON_OPTIONS } from '../FeaturesModal'
import useTranslate from '../../hooks/useTranslate'

import AppsIcon from 'marketing-site/src/images/productIcons/product-apps.svg'
import ArticlesIcon from 'marketing-site/src/images/productIcons/product-articles.svg'
import AtIcon from 'marketing-site/src/images/productIcons/product-at.svg'
import BotsIcon from 'marketing-site/src/images/productIcons/product-bots.svg'
import ContactsIcon from 'marketing-site/src/images/productIcons/product-contacts.svg'
import InboxIcon from 'marketing-site/src/images/productIcons/product-inbox.svg'
import MessengerIcon from 'marketing-site/src/images/productIcons/product-messenger.svg'
import NotificationsIcon from 'marketing-site/src/images/productIcons/product-notifications.svg'
import OutboundIcon from 'marketing-site/src/images/productIcons/product-outbound.svg'
import ProductTourIcon from 'marketing-site/src/images/productIcons/product-product-tours.svg'
import ReportsIcon from 'marketing-site/src/images/productIcons/product-reports.svg'
import SeriesIcon from 'marketing-site/src/images/productIcons/product-series.svg'
import SurveysIconNew from 'marketing-site/src/images/productIcons/product-surveys.svg'
import UsersIcon from 'marketing-site/src/images/productIcons/product-user.svg'
import SmsIcon from 'marketing-site/src/images/productIcons/product-sms.svg'
import { FeatureTooltip } from '../FeatureTooltip'

enum TooltipIcons {
  Outbound,
  Apps,
  Inbox,
  Surveys,
  Bots,
  Reports,
  Series,
  ProductTours,
  Messenger,
  Notifications,
  Contacts,
  User,
  Articles,
  AtIcon,
  Sms,
}
const defaultIcons = {
  [TooltipIcons.Apps]: AppsIcon,
  [TooltipIcons.Articles]: ArticlesIcon,
  [TooltipIcons.AtIcon]: AtIcon,
  [TooltipIcons.Bots]: BotsIcon,
  [TooltipIcons.Contacts]: ContactsIcon,
  [TooltipIcons.Inbox]: InboxIcon,
  [TooltipIcons.Messenger]: MessengerIcon,
  [TooltipIcons.Notifications]: NotificationsIcon,
  [TooltipIcons.Outbound]: OutboundIcon,
  [TooltipIcons.ProductTours]: ProductTourIcon,
  [TooltipIcons.Reports]: ReportsIcon,
  [TooltipIcons.Series]: SeriesIcon,
  [TooltipIcons.Surveys]: SurveysIconNew,
  [TooltipIcons.User]: UsersIcon,
  [TooltipIcons.Sms]: SmsIcon,
}

interface IAddOnTooltips {
  [key: string]: {
    text: string
    eyebrow: string
    icon: TooltipIcons
  }
}

export function FeaturesModalItem({
  feature,
  values,
  showLegend = false,
}: IProps): React.ReactElement | null {
  const icons = defaultIcons

  const featureInPlan = values.find(({ featureKey }) => featureKey.name === feature.name)
  const { translate } = useTranslate()
  const hasNewTooltipModel =
    featureInPlan?.featureKey?.tooltip?.items &&
    featureInPlan?.featureKey?.tooltip?.items.length > 0 &&
    featureInPlan?.featureKey?.tooltip?.title

  if (!featureInPlan) {
    return null
  }

  if (featureInPlan.mainText) {
    return (
      <Text brand="default" size="caption">
        <RichText html={featureInPlan.mainText} />
      </Text>
    )
  }

  if (showLegend && featureInPlan.featureKey.availableInAddOn) {
    const addonEyebrowText = translate('Available with add-on')

    const addOnTooltipProps: IAddOnTooltips = {
      'Proactive Support': {
        text: translate('Proactive Support'),
        eyebrow: addonEyebrowText,
        icon: icons[TooltipIcons.Outbound],
      },
      'Resolution bot': {
        text: translate('Resolution bot'),
        eyebrow: addonEyebrowText,
        icon: icons[TooltipIcons.Bots],
      },
      'Product tours': {
        text: translate('Product tours'),
        eyebrow: addonEyebrowText,
        icon: icons[TooltipIcons.ProductTours],
      },
      WhatsApp: {
        text: 'WhatsApp',
        eyebrow: addonEyebrowText,
        icon: WhatsAppIcon,
      },
      Surveys: {
        text: 'Surveys',
        eyebrow: addonEyebrowText,
        icon: icons[TooltipIcons.Surveys],
      },
      Switch: {
        text: 'Switch',
        eyebrow: addonEyebrowText,
        icon: SwitchIcon,
      },
      Sms: {
        text: 'SMS',
        eyebrow: addonEyebrowText,
        icon: icons[TooltipIcons.Sms],
      },
      PremierOnboarding: {
        text: 'Premier Onboarding',
        eyebrow: addonEyebrowText,
        icon: CustomerService,
      },
      PremierSuccess: {
        text: 'Premier Success',
        eyebrow: addonEyebrowText,
        icon: CustomerService,
      },
      PremierSupport: {
        text: 'Premier Support',
        eyebrow: addonEyebrowText,
        icon: CustomerService,
      },
    }

    const tooltipProps =
      addOnTooltipProps[featureInPlan.featureKey.addOnOption || ADD_ON_OPTIONS.ProcativeSupport]

    /**
     * Supports both versions of Feature Model (one with tooltip model reference and another without)
     * Once we've migrated all Feature Models to this newer one with the tooltip we can ...
     * safely remove the or condition
     */
    return (
      (hasNewTooltipModel && (
        <FeatureTooltip {...featureInPlan.featureKey.tooltip} above>
          {renderIconSVG(Icon.Plus)}
        </FeatureTooltip>
      )) || (
        <Tooltip {...tooltipProps} above>
          {renderIconSVG(Icon.Plus)}
        </Tooltip>
      )
    )
  }

  if (showLegend) {
    return renderIconSVG(Icon.FeatureCheckmark)
  }

  return renderIconSVG(Icon.Checkmark)
}
